<template>
  <div class="profile-page">
    <van-nav-bar fixed title="个人资料" left-text="返回" left-arrow @click-left="goBack" />

    <!-- 头像 -->
    <div class="avatar-card">
      <div class="avatar-wrap" @click="chooseAvatar">
        <img :src="avatarUrl" alt="头像" @error="onAvatarError" />
        <div class="avatar-mask">
          <van-icon name="photograph" />
        </div>
      </div>
      <p class="avatar-tip">{{ uploading ? '头像上传中...' : '点击更换头像' }}</p>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        style="display: none;"
        @change="onFileChange"
      >
    </div>

    <!-- 昵称 / 手机号 -->
    <van-cell-group inset class="form-group">
      <van-field
        v-model="nickname"
        label="昵称"
        placeholder="请输入昵称（2~20 个字符）"
        maxlength="20"
        clearable
      />
      <van-field
        v-model="mobile"
        label="手机号"
        placeholder="请输入 11 位手机号"
        type="tel"
        maxlength="11"
      />
      <van-field
        v-if="mobileChanged"
        v-model="smsCode"
        label="验证码"
        placeholder="请输入短信验证码"
        maxlength="6"
        center
      >
        <template #button>
          <van-button
            size="small"
            type="primary"
            :disabled="!canSendCode || sendingCode || countdown > 0"
            @click="sendCode"
          >
            {{ countdown > 0 ? countdown + 's' : '获取验证码' }}
          </van-button>
        </template>
      </van-field>
    </van-cell-group>
    <p class="field-tip" v-if="mobileChanged">更换手机号需要短信验证码验证，验证码 5 分钟内有效。</p>
    <p class="field-tip" v-else-if="original.mobile">当前绑定手机号：{{ original.mobile }}</p>
    <p class="field-tip" v-else>尚未绑定手机号，绑定后可用手机号 + 密码登录。</p>

    <div class="save-bar">
      <van-button
        type="primary"
        block
        round
        :loading="saving"
        loading-text="保存中..."
        @click="save"
      >保存</van-button>
    </div>
  </div>
</template>


<script>
import { Toast } from 'vant'
import {
  getUserInfoDetail,
  updateProfileApi,
  sendBindCodeApi,
  bindMobileApi,
  uploadAvatarApi
} from '@/api/user'

// 本地兜底头像（用户未设置头像 / 头像加载失败时使用）
const DEFAULT_AVATAR = require('@/assets/default-avatar.svg')

export default {
  name: 'Profile',
  data () {
    return {
      // 已保存的资料（用于比对哪些字段被修改）
      original: { nickname: '', avatar: '', mobile: '' },
      nickname: '',
      avatar: '',
      mobile: '',
      smsCode: '',
      uploading: false,
      saving: false,
      sendingCode: false,
      countdown: 0,
      countdownTimer: null,
      defaultAvatar: DEFAULT_AVATAR
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    avatarUrl () {
      return this.avatar || this.original.avatar || this.defaultAvatar
    },
    nicknameChanged () {
      return this.nickname.trim() !== (this.original.nickname || '')
    },
    avatarChanged () {
      return (this.avatar || '') !== (this.original.avatar || '')
    },
    mobileChanged () {
      return this.mobile.trim() !== (this.original.mobile || '')
    },
    canSendCode () {
      return /^1[3-9]\d{9}$/.test(this.mobile.trim())
    }
  },
  created () {
    if (!this.isLogin) {
      this.$router.replace('/login?redirect=/profile')
      return
    }
    this.loadUserInfo()
  },
  beforeDestroy () {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/user')
      }
    },
    async loadUserInfo () {
      try {
        const { data } = await getUserInfoDetail()
        this.original = {
          nickname: data.nickname || '',
          avatar: data.avatar || '',
          mobile: data.mobile || ''
        }
        this.nickname = this.original.nickname
        this.avatar = this.original.avatar
        this.mobile = this.original.mobile
        // 同步到 vuex（并写入本地缓存）
        this.$store.commit('user/setUserInfo', { ...this.$store.state.user.userInfo, ...data })
      } catch (err) {
        Toast(typeof err === 'string' ? err : '获取用户信息失败')
      }
    },
    chooseAvatar () {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },
    // 选择图片 -> 上传 -> 记录头像地址（点击「保存」时统一提交）
    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        Toast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        Toast('头像图片不能超过 5MB')
        return
      }
      this.uploading = true
      try {
        const res = await uploadAvatarApi(file)
        this.avatar = (res.data && res.data.url) || ''
        Toast('头像已上传，点击「保存」生效')
      } catch (err) {
        Toast(typeof err === 'string' ? err : '头像上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },
    onAvatarError () {
      if (this.avatar) this.avatar = ''
      if (this.original.avatar) this.original.avatar = ''
    },
    // 获取绑定手机号验证码
    async sendCode () {
      const mobile = this.mobile.trim()
      if (!/^1[3-9]\d{9}$/.test(mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (!this.mobileChanged) {
        Toast('手机号未发生变化，无需验证')
        return
      }
      this.sendingCode = true
      try {
        const res = await sendBindCodeApi(mobile)
        Toast(res.message || '验证码已发送')
        // 演示模式（未配置短信服务）下后端会返回验证码，便于本地调试
        if (res.data && res.data.demo_code) {
          Toast('演示验证码：' + res.data.demo_code)
        }
        this.startCountdown()
      } catch (err) {
        Toast(typeof err === 'string' ? err : '验证码发送失败')
      } finally {
        this.sendingCode = false
      }
    },
    startCountdown () {
      this.countdown = 60
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    // 保存：按修改的字段分别提交（头像 / 昵称走资料接口，手机号走绑定接口）
    async save () {
      if (!this.nicknameChanged && !this.avatarChanged && !this.mobileChanged) {
        Toast('资料没有变化')
        return
      }
      if (this.nicknameChanged) {
        const len = this.nickname.trim().length
        if (len < 2 || len > 20) {
          Toast('昵称需要 2~20 个字符')
          return
        }
      }
      if (this.mobileChanged) {
        if (!this.canSendCode) {
          Toast('请输入正确的手机号')
          return
        }
        if (!this.smsCode.trim()) {
          Toast('请输入短信验证码')
          return
        }
      }

      this.saving = true
      try {
        // 1. 昵称 / 头像
        if (this.nicknameChanged || this.avatarChanged) {
          const payload = {}
          if (this.nicknameChanged) payload.nickname = this.nickname.trim()
          if (this.avatarChanged) payload.avatar = this.avatar
          const res = await updateProfileApi(payload)
          if (res.data) {
            this.original.nickname = res.data.nickname || this.original.nickname
            this.original.avatar = res.data.avatar || ''
            this.nickname = this.original.nickname
            this.avatar = this.original.avatar
            this.$store.commit('user/setUserInfo', { ...this.$store.state.user.userInfo, ...res.data })
          }
        }
        // 2. 手机号
        if (this.mobileChanged) {
          const res = await bindMobileApi(this.mobile.trim(), this.smsCode.trim())
          this.original.mobile = (res.data && res.data.mobile) || this.mobile.trim()
          this.mobile = this.original.mobile
          this.smsCode = ''
          this.$store.commit('user/setUserInfo', {
            ...this.$store.state.user.userInfo,
            mobile: this.original.mobile
          })
        }
        Toast.success('保存成功')
        this.$router.replace('/user')
      } catch (err) {
        Toast(typeof err === 'string' ? err : '保存失败，请重试')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.profile-page {
  min-height: 100vh;
  background-color: #f7f7f7;
  padding-top: 46px;

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  /deep/ .van-nav-bar { z-index: 100; }

  .avatar-card {
    background: #fff;
    padding: 22px 0 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .avatar-wrap {
      position: relative;
      width: 90px;
      height: 90px;
      border-radius: 50%;
      overflow: hidden;
      background: #f2f2f2;
      cursor: pointer;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .avatar-mask {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 26px;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
      }
    }

    .avatar-tip {
      margin-top: 10px;
      font-size: 13px;
      color: #999;
    }
  }

  .form-group {
    margin-top: 12px;
  }

  .field-tip {
    margin: 10px 20px 0;
    font-size: 12px;
    color: #999;
    line-height: 1.6;
  }

  .save-bar {
    padding: 24px 16px 30px;

    /deep/ .van-button--primary {
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border: none;
    }
  }
}
</style>

